import type { IsoDate, Measurement } from "./types.js";
import type { RacePredictionRow, RaceResultRow } from "../../shared/types.js";
import { SEASON_2 } from "../../shared/plan.js";
import { assignRanks } from "./settlement.js";

/**
 * 台北馬賽季競猜的結算。完賽與體重**兩題分開**，各有一份排名，只收該題答對的人。
 *
 * 與 `settlement.ts`（生日賽季）分開：那一季只有一題，而且已經封盤。
 * 與 `src/domain/` 的鐵則一致：不讀系統時間，「今天」由呼叫端傳入。
 */

export type FinishOutcome = "pending" | "finished" | "not_finished" | "cancelled";

export interface RankedBet {
  nickname: string;
  /** 與結果的差距。沒填數字是 null。 */
  diff: number | null;
  rank: number | null;
}

export interface WeightSettlement {
  /** 還沒到賽事目標日、或完全沒有體重時是 false。 */
  settled: boolean;
  finalWeight: number | null;
  finalDate: IsoDate | null;
  reached: boolean | null;
  ranking: RankedBet[];
}

export interface FinishSettlement {
  outcome: FinishOutcome;
  finishSeconds: number | null;
  /** 只有結果出來、而且沒有取消時才有人。 */
  ranking: RankedBet[];
}

export interface RaceSettlement {
  weight: WeightSettlement;
  finish: FinishSettlement;
}

/**
 * 完賽與否由官方時間推導，沒有任何一個欄位直接宣告「完賽了」。
 *
 * 取消優先：賽事取消時就算誤填了時間，這一題也作廢。
 */
export function finishOutcome(result: RaceResultRow | null): FinishOutcome {
  if (!result) return "pending";
  if (result.cancelled) return "cancelled";
  return result.finish_seconds === null ? "not_finished" : "finished";
}

/** 有數字的照誤差排在前面，沒填的照名字排在後面並列。 */
function rank(bets: RankedBet[]): RankedBet[] {
  return assignRanks([
    ...bets.filter(row => row.diff !== null).sort((a, b) => (a.diff as number) - (b.diff as number)),
    ...bets.filter(row => row.diff === null).sort((a, b) => a.nickname.localeCompare(b.nickname))
  ]);
}

/** 浮點數的 0.30000000000000004 不該讓兩個一樣準的人分出名次。 */
const roundDiff = (value: number) => Math.round(value * 1000) / 1000;

function settleWeight(
  predictions: RacePredictionRow[],
  measurements: Measurement[],
  today: IsoDate
): WeightSettlement {
  /**
   * 12/20 那筆，沒有就取賽季內最後一筆。賽季之後才量的不算，否則結算會隨著之後
   * 的量測一直變。
   */
  const final = today < SEASON_2.end ? null : measurements
    .filter(row => row.measurement_date <= SEASON_2.end && Number.isFinite(row.weight_kg))
    .sort((a, b) => a.measurement_date.localeCompare(b.measurement_date))
    .at(-1) ?? null;

  if (!final) {
    return { settled: false, finalWeight: null, finalDate: null, reached: null, ranking: [] };
  }

  const finalWeight = final.weight_kg as number;
  // 第二季是「等於或低於」就算達標，與第一季的「低於」不同，見 CONTEXT.md
  const reached = finalWeight <= SEASON_2.targetWeight;

  return {
    settled: true,
    finalWeight,
    finalDate: final.measurement_date,
    reached,
    ranking: rank(predictions
      .filter(bet => !!bet.will_reach_weight === reached)
      .map(bet => ({
        nickname: bet.nickname,
        diff: bet.weight_kg === null ? null : roundDiff(Math.abs(bet.weight_kg - finalWeight)),
        rank: null
      })))
  };
}

function settleFinish(
  predictions: RacePredictionRow[],
  result: RaceResultRow | null
): FinishSettlement {
  const outcome = finishOutcome(result);
  const finishSeconds = outcome === "finished" ? (result as RaceResultRow).finish_seconds : null;

  if (outcome === "pending" || outcome === "cancelled") {
    return { outcome, finishSeconds: null, ranking: [] };
  }

  const finished = outcome === "finished";
  return {
    outcome,
    finishSeconds,
    ranking: rank(predictions
      .filter(bet => !!bet.will_finish === finished)
      .map(bet => ({
        nickname: bet.nickname,
        // 沒完賽就沒有時間可以比，答對「不會完賽」的人都並列
        diff: finishSeconds === null || bet.finish_seconds === null
          ? null
          : Math.abs(bet.finish_seconds - finishSeconds),
        rank: null
      })))
  };
}

export function settleRace(
  predictions: RacePredictionRow[],
  measurements: Measurement[],
  result: RaceResultRow | null,
  today: IsoDate
): RaceSettlement {
  return {
    weight: settleWeight(predictions, measurements, today),
    finish: settleFinish(predictions, result)
  };
}
